import { useMutation, useQuery } from "@tanstack/react-query";

import {
  BOOK_COMPONENT_CATEGORY_IDS,
  CALL_API_ENDPOINT,
  INDIA_COUNTRY_ID,
  KH_PRODUCT_ID,
  KH_TENANT_ID,
  KH_VERTICAL_ID,
  LEARN_VERTICAL_ID,
  LOGIN_PRODUCT_ID,
  LOGIN_TENANT_ID,
  PLATFORM,
  REQUEST_PRODUCT_ID,
  ROLES_FOR_SEARCHING_AGENT,
  DISCOUNT_TYPE,
  PLUTUS_SOURCE_ID,
  SCA_ONE_APP_MIGRATED,
  VALID_SALES_ROLE_IDS,
  X_TENANT,
} from "constants/config";
import {
  getCountryIdFromUrl,
  getLearnPackagesUrl,
  getOneOmsBaseUrl,
  getPackagesAndSubscriptionsUrl,
  getScaPackagesUrl,
  getSearchUserUrl,
} from "constants/omsRegionConfig";
import { DAY, HOUR, QUERY_RETRY_ATTEMPTS } from "constants/queryCacheTimes";
import fetchJSON from "hooks/restApiClient";
import { getAuthToken } from "utils/tokenUtils";

let activeGatewayTenantId = null;

const setActiveGatewayTenantId = (gatewayTenantId) => {
  if (gatewayTenantId != null && gatewayTenantId !== "") {
    activeGatewayTenantId = gatewayTenantId;
  }
};

export const gatewayTenantHeader = () =>
  activeGatewayTenantId ? { "x-tenant-id": activeGatewayTenantId } : {};

const oneOmsHeaders = (overrides = {}) => {
  setActiveGatewayTenantId(overrides["x-tenant-id"]);

  return {
    accept: "application/json",
    "auth-token": getAuthToken(),
    "Content-Type": "application/json",
    ...gatewayTenantHeader(),
    ...overrides,
  };
};

const calculateBookPrice = (pkg, subscriptionId) => {
  const bookComponentsFromSubscriptions = pkg.packageSubscriptions
    .flatMap((subscription) => subscription.components)
    .filter(
      (component) =>
        BOOK_COMPONENT_CATEGORY_IDS.includes(
          component.componentCategory.componentCategoryId,
        ) &&
        component.bookPrice &&
        component.packageSubscriptionId === subscriptionId,
    )
    .reduce(
      (acc, component) => ({
        ...acc,
        [component.componentId]: component.bookPrice,
      }),
      {},
    );

  const bookComponentsFromPackage = (pkg.bookComponents || [])
    .filter(
      (component) =>
        BOOK_COMPONENT_CATEGORY_IDS.includes(
          component.componentCategory.componentCategoryId,
        ) &&
        component.bookPrice &&
        component.packageSubscriptionId === subscriptionId,
    )
    .reduce(
      (acc, component) => ({
        ...acc,
        [component.componentId]: component.bookPrice,
      }),
      {},
    );

  const bookPrices = {
    ...bookComponentsFromSubscriptions,
    ...bookComponentsFromPackage,
  };

  const totalBookPrice = Object.values(bookPrices).reduce(
    (total, price) => total + price,
    0,
  );

  return totalBookPrice;
};

const getAllComponentProductTypeIds = (pkg, subscription) => {
  const subscriptionComponentProductTypeIds = subscription.components.map(
    (component) => component.componentProduct.componentProductTypeId,
  );

  const bookComponentProductTypeIds = (pkg.bookComponents || [])
    .filter(
      (component) =>
        component.packageSubscriptionId === subscription.packageSubscriptionId,
    )
    .map((component) => component.componentProduct.componentProductTypeId);

  return [
    ...subscriptionComponentProductTypeIds,
    ...bookComponentProductTypeIds,
  ];
};

const getAllComponentCategoryIds = (pkg, subscription) => {
  const subscriptionComponentCategoryIds = subscription.components.map(
    (component) => component.componentCategory?.componentCategoryId,
  );

  const bookComponentCategoryIds = (pkg.bookComponents || [])
    .filter(
      (component) =>
        component.packageSubscriptionId === subscription.packageSubscriptionId,
    )
    .map((component) => component.componentCategory?.componentCategoryId);

  return [...subscriptionComponentCategoryIds, ...bookComponentCategoryIds];
};

const getAcademicYearForKH = (pkg) => {
  if (pkg.verticalId === KH_VERTICAL_ID && pkg.academicYear) {
    return pkg.academicYear;
  }
  return null;
};

const fetchPaymentHeadsForSubscription = async (
  subscriptionIds,
  gatewayTenantId,
) => {
  if (gatewayTenantId != null && gatewayTenantId !== "") {
    setActiveGatewayTenantId(gatewayTenantId);
  }

  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/does-subscription-payment-heads-exist`,
  );

  const payload = {
    subscription_ids: subscriptionIds,
  };
  if (gatewayTenantId != null && gatewayTenantId !== "") {
    payload.gateway_tenant_id = gatewayTenantId;
  }

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const options = {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  };

  const response = await fetch(url, options);

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useCheckPaymentHeadsForSubscription = (
  subscriptionIds,
  gatewayTenantId,
) => {
  return useQuery({
    queryKey: ["subscriptionIds", subscriptionIds, gatewayTenantId],
    queryFn: () =>
      fetchPaymentHeadsForSubscription(subscriptionIds, gatewayTenantId),
    enabled: !!subscriptionIds,
  });
};
const fetchPackages = async (filters) => {
  setActiveGatewayTenantId(filters.gatewayTenantId);

  const url =
    filters.verticalId === LEARN_VERTICAL_ID
      ? new URL(getLearnPackagesUrl())
      : filters.verticalId === KH_VERTICAL_ID
        ? new URL(getScaPackagesUrl())
        : null;

  if (!url) {
    throw new Error("Invalid verticalId provided");
  }
  const rolesString = localStorage.getItem("roles");
  const roles = rolesString
    ? rolesString.split(",").map((role) => role.trim())
    : [];

  const validPageUrls = roles
    .map((roleId) => VALID_SALES_ROLE_IDS[Number(roleId)])
    .find((pageUrl) => pageUrl !== undefined);

  const params = {
    exams: (filters.exams || []).join(","),
    grade: (filters.grade || []).join(","),
    producttype: PRODUCT_TYPES.map((type) => type.value).join(","),
    pageUrl: validPageUrls,
    verticalId: filters.verticalId,
  };

  Object.keys(params).forEach((key) => {
    if (params[key]) {
      url.searchParams.append(key, params[key]);
    }
  });

  const headers = {
    ...gatewayTenantHeader(),
    "x-tenant": X_TENANT,
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Unknown error occurred");
  }

  const data = await response.json();

  if (data.status && data.message === "success") {
    const formattedData = data.data
      .filter((pkg) => pkg.packageId !== null)
      .flatMap((pkg) =>
        pkg.packageSubscriptions.map((subscription) => ({
          id: subscription.packageSubscriptionId,
          verticalId: pkg.verticalId,
          packageId: pkg.packageId,
          displayName: pkg.displayName,
          planName: subscription.planName,
          planValidity: pkg.packageValidityDate,
          mrp: subscription.packagePaymentPlan.mrp,
          booksPrice: calculateBookPrice(
            pkg,
            subscription.packageSubscriptionId,
          ),
          componentProductTypeIds: getAllComponentProductTypeIds(
            pkg,
            subscription,
          ),
          componentCategoryIds: getAllComponentCategoryIds(pkg, subscription),
          academicYear: getAcademicYearForKH(pkg),
        })),
      );
    const subscriptionIds = formattedData.map((item) => item.id);

    if (subscriptionIds.length === 0) {
      throw new Error("No packages found.");
    }
    const paymentHeadsExist = await (async () => {
      try {
        return await fetchPaymentHeadsForSubscription(
          subscriptionIds,
          filters.gatewayTenantId,
        );
      } catch (e) {
        throw e;
      }
    })();

    const packagesWithHeads = formattedData.filter((pkg) => {
      return paymentHeadsExist[pkg.id] === true;
    });
    return packagesWithHeads;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchPackages = (filters, enabled = true) => {
  return useQuery({
    queryKey: ["packages", filters],
    queryFn: () => fetchPackages(filters),
    enabled,
  });
};

const buildPackagesAndSubscriptionsPayload = (subscriptions) => ({
  packages: Object.values(
    (subscriptions ?? []).reduce((packagesMap, subscription) => {
      const existing = packagesMap[subscription.packageId];
      return {
        ...packagesMap,
        [subscription.packageId]: existing
          ? {
              ...existing,
              subscriptionIds: [...existing.subscriptionIds, subscription.id],
            }
          : {
              packageId: subscription.packageId,
              subscriptionIds: [subscription.id],
            },
      };
    }, {}),
  ),
});

const getBooksPriceMapFromOmsResponse = (omsResponse) =>
  (omsResponse?.data || []).reduce((booksPriceMap, pkg) => {
    const subscriptionPrices = (pkg.packageSubscriptions || []).reduce(
      (acc, subscription) => ({
        ...acc,
        [subscription.packageSubscriptionId]: calculateBookPrice(
          pkg,
          subscription.packageSubscriptionId,
        ),
      }),
      {},
    );
    return { ...booksPriceMap, ...subscriptionPrices };
  }, {});

const fetchPackagesAndSubscriptions = async (payload) => {
  const url = new URL(getPackagesAndSubscriptionsUrl());

  const headers = {
    ...gatewayTenantHeader(),
    accept: "*/*",
    Authorization: process.env.REACT_APP_OMS_PACKAGE_SUBSCRIPTION_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok || !data.status) {
    throw new Error(
      data.message || "Failed to fetch package and subscriptions information",
    );
  }

  return data;
};

const enrichPackageSubscriptionsWithOmsBookPrices = (
  subscriptions,
  omsResponse,
) => {
  const booksPriceMap = getBooksPriceMapFromOmsResponse(omsResponse);
  return subscriptions.map((subscription) => ({
    ...subscription,
    booksPrice: booksPriceMap[subscription.id] ?? subscription.booksPrice ?? 0,
  }));
};

const fetchEnrichedPackageSubscriptions = async (subscriptions) => {
  if (!subscriptions?.length) {
    return subscriptions ?? [];
  }

  const payload = buildPackagesAndSubscriptionsPayload(subscriptions);
  const omsResponse = await fetchPackagesAndSubscriptions(payload);
  return enrichPackageSubscriptionsWithOmsBookPrices(subscriptions, omsResponse);
};

export const useFetchPackagesAndSubscriptions = (subscriptions, options = {}) => {
  const { enabled = false, countryId } = options;
  const payload = buildPackagesAndSubscriptionsPayload(subscriptions ?? []);

  return useQuery({
    queryKey: ["packagesAndSubscriptions", payload, countryId],
    queryFn: () => fetchEnrichedPackageSubscriptions(subscriptions),
    enabled: enabled && !!payload.packages.length,
    staleTime: HOUR,
    cacheTime: HOUR,
    retry: false,
  });
};

const fetchPostalCodeDetails = async (postalCode, gatewayTenantId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-postal-code-details/${postalCode}`,
  );

  const headers = oneOmsHeaders(
    gatewayTenantId != null && gatewayTenantId !== ""
      ? { "x-tenant-id": gatewayTenantId }
      : {},
  );

  const response = await fetch(url, { headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(`Error: ${data.message}`);
  }

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchPostalCodeDetails = (postalCode, gatewayTenantId) => {
  const isQueryEnabled = postalCode && postalCode.length === 6;
  return useQuery({
    queryKey: ["postalCodeDetails", postalCode, gatewayTenantId],
    queryFn: () => fetchPostalCodeDetails(postalCode, gatewayTenantId),
    enabled: !!isQueryEnabled,
  });
};

const buildApplyDiscountSkus = (skus = []) =>
  skus.map(({ id, subscriptions = [] }) => ({
    id,
    subscriptions: subscriptions.map(({ id: subscriptionId }) => ({
      id: subscriptionId,
    })),
  }));

const formatApplyDiscountValue = (discount) => {
  if (discount?.discount_value == null) {
    return null;
  }

  return discount.is_percentage
    ? `${discount.discount_value}%`
    : String(discount.discount_value);
};

const buildApplyCouponDiscountPayload = (payload) => {
  const { skus, discount } = payload;
  const requestBody = {
    skus: buildApplyDiscountSkus(skus),
    coupon_code: discount.coupon_code,
    customer_id: discount.customer_id,
  };

  const discountValue = formatApplyDiscountValue(discount);
  if (discountValue) {
    requestBody.discount = discountValue;
  }

  return requestBody;
};

const buildApplyDynamicDiscountPayload = (payload) => {
  const { skus, discount } = payload;
  const discountValue = formatApplyDiscountValue(discount);

  if (!discountValue) {
    throw new Error("Discount value is required to apply dynamic discount.");
  }

  return {
    skus: buildApplyDiscountSkus(skus),
    discount: discountValue,
  };
};

const fetchUpdatedPrice = async (payload) => {
  const { gateway_tenant_id, discount } = payload || {};

  if (gateway_tenant_id == null) {
    throw new Error("Tenant is required to fetch price.");
  }

  if (!discount?.discount_type) {
    throw new Error("Discount type is required to fetch price.");
  }

  let url;
  let requestBody;

  if (discount.discount_type === DISCOUNT_TYPE.COUPON_CODE) {
    url = `${getOneOmsBaseUrl()}/apply-coupon-discount`;
    requestBody = buildApplyCouponDiscountPayload(payload);
  } else if (discount.discount_type === DISCOUNT_TYPE.DYNAMIC) {
    url = `${getOneOmsBaseUrl()}/apply-dynamic-discount`;
    requestBody = buildApplyDynamicDiscountPayload(payload);
  } else {
    throw new Error(`Unsupported discount type: ${discount.discount_type}`);
  }

  const headers = oneOmsHeaders({
    "x-source-id": PLUTUS_SOURCE_ID,
    "x-tenant-id": gateway_tenant_id,
    "x-platform": "web",
  });

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestBody),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.message || "An error occurred while processing the request.",
    );
  }

  if (data.status && data.status === "success") {
    return data.data;
  }

  throw new Error(
    data?.message || "An error occurred while processing the request.",
  );
};

export const useFetchUpdatedPrice = (payload) => {
  return useQuery({
    queryKey: ["discount", payload],
    queryFn: () => fetchUpdatedPrice(payload),
    enabled: false,
    retry: false,
    staleTime: 0,
  });
};

const punchOrder = async (payload) => {
  const url = new URL(`${getOneOmsBaseUrl()}/punch-order-v2`);

  const { gateway_tenant_id, ...requestBody } = payload || {};

  if (gateway_tenant_id == null) {
    throw new Error("Tenant is required to punch the order.");
  }

  const headers = oneOmsHeaders({
    "x-source-id": PLUTUS_SOURCE_ID,
    "x-tenant-id": gateway_tenant_id,
    "x-platform": "web",
  });

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(requestBody),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }

  if (data.status === "success") {
    return data.data.order_id;
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const usePunchOrder = () => {
  return useMutation({ mutationFn: punchOrder });
};

const fetchCustomerOrderDetails = async (uam_id, countryId) => {
  const url = new URL(
    `${getOneOmsBaseUrl({ countryId })}/fetch-customer-orders-details`,
  );

  const headers = oneOmsHeaders();

  const payload = { uam_id };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchCustomerOrderDetails = (payload) => {
  const isDisabled = payload.uamId !== null && payload.uamId !== undefined;
  const { uamId: uam_id, countryId } = payload;
  return useQuery({
    queryKey: ["customer_order_details", uam_id, countryId],
    queryFn: () => fetchCustomerOrderDetails(uam_id, countryId),
    enabled: !!isDisabled,
  });
};

const fetchSelectedOrderDetails = async (order_id, options = {}) => {
  const url = new URL(
    `${getOneOmsBaseUrl({ countryId: options.countryId })}/get-order-info/${order_id}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, { headers });

  const data = await response.json();

  if (data.status && data.status === "success") {
    const orderDetails = data.data.order_details;
    setActiveGatewayTenantId(orderDetails?.gateway_tenant_id);
    return orderDetails;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useSelectedOrderDetails = (order_id, options = {}) => {
  const countryId =
    options.countryId != null && options.countryId !== ""
      ? options.countryId
      : getCountryIdFromUrl();

  return useQuery({
    queryKey: ["selected_order_id", order_id, countryId],
    queryFn: () => fetchSelectedOrderDetails(order_id, { countryId }),
    enabled: options.enabled !== false && !!order_id,
  });
};

const transformKHUserToUserFormat = (khUser) => {
  return [
    {
      firstName: khUser.first_name,
      lastName: khUser.last_name,
      phone: khUser.phone,
      uamId: khUser.uam_id,
      verticalId: KH_VERTICAL_ID,
    },
  ];
};

const extractUserDetails = (globalUsers, selectedBusinessVertical) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  if (!localStorage.getItem("phoneNumber")) {
    throw new Error(
      "Oops! Something doesn't look right. Please log out and log in again to continue.",
    );
  }

  const isSameUser = globalUsers.accounts.some(
    (account) => account.phone === localStorage.getItem("phoneNumber"),
  );

  if (isSameUser) {
    throw new Error(
      "Seems like you are creating an order for yourself. Please select a different user to create an order.",
    );
  }

  const filteredUsersByVertical = globalUsers.accounts.flatMap((account) =>
    account.profiles
      .filter(
        (profile) =>
          Number(profile.vertical_id) === Number(selectedBusinessVertical),
      )
      .map((profile) => ({
        firstName: account.first_name,
        lastName: account.last_name,
        phone: account.phone,
        verticalId: profile.vertical_id,
        uamId: profile.uam_id,
      })),
  );

  if (filteredUsersByVertical.length === 0) {
    throw new Error("No user found for the selected business vertical.");
  }

  return filteredUsersByVertical;
};

const fetchCustomerInfoFromOneApp = async (payload) => {
  const { phone_number, uam_id, selectedBusinessVertical, isd_code } = payload;

  const url = getSearchUserUrl({
    isdCode: isd_code,
    countryId: payload.country_id,
  });

  const requestPayload = phone_number
    ? { isd_code: isd_code, phone: phone_number }
    : { uam_id: Number(uam_id) };

  const headers = {
    ...gatewayTenantHeader(),
    Accept: "application/json",
    "Content-Type": "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch customer info: ${response.status} ${errorText}`,
    );
  }

  const responseData = await response.json();

  return extractUserDetails(responseData, selectedBusinessVertical);
};

const formatKHUserToUserFormat = (users) => {
  const formattedUsers = users.map((account) => {
    return {
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: KH_VERTICAL_ID,
      uamId: account.uam_id,
    };
  });

  return formattedUsers;
};
const fetchKHCustomerInfo = async (payload) => {
  const url = new URL(process.env.REACT_APP_GET_VERIFY_USER);

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": "900",
    platform: "web",
    "tenant-id": "7",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const requestPayload = payload.phone_number
    ? {
        isd_code: payload.isd_code,
        phone: payload.phone_number,
      }
    : { uam_id: payload.uam_id };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }

  if (!response.ok) {
    throw new Error(
      "Failed to fetch KH customer info, please try again later.",
    );
  }

  const data = await response.json();

  return formatKHUserToUserFormat(data.users);
};

const fetchCustomerInfoSeparately = async (payload) => {
  if (Number(payload.selectedBusinessVertical) === Number(LEARN_VERTICAL_ID)) {
    return fetchCustomerInfoFromOneApp(payload);
  }

  return fetchKHCustomerInfo(payload);
};

const fetchCustomerInfo = async (payload) => {
  const customerInfo =
    SCA_ONE_APP_MIGRATED === true
      ? await fetchCustomerInfoFromOneApp(payload)
      : await fetchCustomerInfoSeparately(payload);

  return customerInfo;
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};

const discardOrder = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/discard-order/${orderId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    throw new Error(`Failed to discard order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useDiscardOrder = () => {
  return useMutation({ mutationFn: discardOrder });
};

const createPaymentLink = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/create-payment-link`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail.map((err) => err.msg).join("\n");
    throw new Error(messages);
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const useCreatePaymentLink = () => {
  return useMutation({ mutationFn: createPaymentLink });
};

const requestCancelOrder = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/request-cancellation`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || ` ${response.status}`);
  }

  const data = await response.json();
  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useRequestCancelOrder = () => {
  return useMutation({ mutationFn: requestCancelOrder });
};

const punchPayment = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/punch-payment-v2`,
  );
  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail
      .map((errorDetail) => errorDetail.msg)
      .join("\n");
    const err = new Error(messages);
    if (data.error_code) err.error_code = data.error_code;
    throw err;
  }
  const err = new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
  if (data?.error_code) err.error_code = data.error_code;
  throw err;
};

export const usePunchPayment = () => {
  return useMutation({ mutationFn: punchPayment });
};

const fetchPendingOrders = async ({ sales_person_id, countryId }) => {
  const url = new URL(
    `${getOneOmsBaseUrl({ countryId })}/get-pending-orders-details/${sales_person_id}`,
  );

  const headers = oneOmsHeaders({ "x-source-id": PLUTUS_SOURCE_ID });

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch pending order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders.",
    );
  }
};

export const useFetchPendingOrders = (
  sales_person_id,
  { countryId, enabled = false } = {},
) => {
  return useQuery({
    queryKey: ["pendingOrders", sales_person_id, countryId],
    queryFn: () => fetchPendingOrders({ sales_person_id, countryId }),
    enabled: Boolean(enabled && sales_person_id && countryId != null),
  });
};

export const GRADES = [
  { key: 1, label: "Grade 1", value: "Grade 1" },
  { key: 2, label: "Grade 2", value: "Grade 2" },
  { key: 3, label: "Grade 3", value: "Grade 3" },
  { key: 4, label: "Grade 4", value: "Grade 4" },
  { key: 5, label: "Grade 5", value: "Grade 5" },
  { key: 6, label: "Grade 6", value: "Grade 6" },
  { key: 7, label: "Grade 7", value: "Grade 7" },
  { key: 8, label: "Grade 8", value: "Grade 8" },
  { key: 9, label: "Grade 9", value: "Grade 9" },
  { key: 10, label: "Grade 10", value: "Grade 10" },
  { key: 11, label: "Grade 11", value: "Grade 11" },
  { key: 12, label: "Grade 12", value: "Grade 12" },
  { key: 13, label: "Grade 13", value: "Grade 13" },
];

export const TARGET_EXAMS = [
  { key: 1, label: "FOUNDATIONTEST", value: "FOUNDATIONTEST" },
  { key: 2, label: "NEET", value: "NEET" },
  { key: 3, label: "JEEMAIN", value: "JEEMAIN" },
  { key: 4, label: "JEEMAIN,JEEADVANCED", value: "JEEMAIN,JEEADVANCED" },
  { key: 5, label: "CBSE", value: "CBSE" },
  { key: 6, label: "ICSE", value: "ICSE" },
  { key: 7, label: "Tamil Nadu Board", value: "School Exams (TN Board)" },
  { key: 8, label: "Maharashtra Board", value: "Maharashtra Board" },
  { key: 9, label: "Telangana Board", value: "Telangana Board" },
  { key: 10, label: "Andhra Pradesh Board", value: "Andhra Pradesh Board" },
  {
    key: 11,
    label: "Jammu and Kashmir Board",
    value: "Jammu and Kashmir Board",
  },
  { key: 12, label: "HOTS", value: "HOTS" },
  { key: 13, label: "MATHS CHAMP", value: "MATHS" },
  { key: 14, label: "EEP", value: "EEP" },
  { key: 15, label: "APTITUDETEST", value: "APTITUDETEST" },
  {
    key: 16,
    label: "JEEMAIN,JEEADVANCED,CBSE,NEET",
    value: "JEEMAIN,JEEADVANCED,CBSE,NEET",
  },
  { key: 17, label: "MHCET", value: "MHCET" },
  { key: 18, label: "EAMCET", value: "EAMCET" },
  { key: 19, label: "KCET", value: "KCET" },
  { key: 20, label: "NEST", value: "NEST" },
  { key: 21, label: "US CCSS", value: "US CCSS" },
  { key: 22, label: "RMO", value: "RMO" },
];

export const PRODUCT_TYPES = [
  { key: 1, label: "Assessment", value: "Assessment" },
  { key: 2, label: "liveclasses", value: "liveclasses" },
  { key: 3, label: "selflearn", value: "selflearn" },
  { key: 4, label: "Doubts", value: "Doubts" },
  { key: 5, label: "recordedCourse", value: "recordedCourse" },
  { key: 6, label: "Hardware", value: "Hardware" },
  { key: 7, label: "Support", value: "Support" },
];

export const useOrderCancelReasons = () => ({
  isPending: false,
  data: [
    {
      id: 1,
      value: "1",
      label: "Customer Centric",
    },
    {
      id: 2,
      value: "2",
      label: "Sales User Centric",
    },
    {
      id: 3,
      value: "3",
      label: "Finance Partner Centric",
    },
  ].map((type) => ({
    id: type.id,
    value: type.value,
    label: type.label,
  })),
});

const fetchLoanProviderDetails = async (verticalId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-loan-provider-details-by-vertical-id/${verticalId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error("Unexpected API response format");
  }
};

export const useFetchLoanProviderDetails = (verticalId) => {
  return useQuery({
    queryKey: ["loanProviders", verticalId],
    queryFn: () => fetchLoanProviderDetails(verticalId),
    enabled: Boolean(verticalId),
  });
};

const addNote = async (payload) => {
  const url = new URL(`${getOneOmsBaseUrl()}/add-note`);

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to add note to the order: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useAddNote = () => {
  return useMutation({ mutationFn: addNote });
};

const fetchPaymentReceipt = async (paymentId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-payment-receipt/${paymentId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch payment receipt. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the payment receipt.",
    );
  }
};

export const useFetchPaymentReceipt = (paymentId) => {
  const query = useQuery({
    queryKey: ["paymentReceipt", paymentId],
    queryFn: () => fetchPaymentReceipt(paymentId),
    enabled: !!paymentId,
  });
  return {
    ...query,
    fetchPaymentReceipt,
  };
};

const fetchOrderSummaryPDF = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-order-summary/${orderId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchOrderSummaryPDF = (orderId) => {
  return useQuery({
    queryKey: ["OrderSummary", orderId],
    queryFn: () => fetchOrderSummaryPDF(orderId),
    enabled: false,
    retry: false,
  });
};

const fetchTaxInvoicePDF = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-tax-invoice/${orderId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice.",
    );
  }
};

export const useFetchTaxInvoicePDF = (orderId) => {
  const query = useQuery({
    queryKey: ["taxInvoice", orderId],
    queryFn: () => fetchTaxInvoicePDF(orderId),
    enabled: !!orderId,
  });
  return {
    ...query,
    fetchTaxInvoicePDF,
  };
};

const requestTransferOrder = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/create-order-modification-request-v2`,
  );

  const { gateway_tenant_id, ...requestBody } = payload || {};

  if (gateway_tenant_id == null) {
    throw new Error("Tenant is required to modify the order.");
  }

  const headers = oneOmsHeaders({
    "x-platform": "web",
    "x-source-id": PLUTUS_SOURCE_ID,
    "x-tenant-id": gateway_tenant_id,
  });

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || ` ${response.status}`);
  }
  const data = await response.json();

  if (data.status === "success") {
    return data.data.order_proposal_id;
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const useRequestTransferOrder = () => {
  return useMutation({ mutationFn: requestTransferOrder });
};

const fetchAllPayments = async (order_id) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-all-payments/${order_id}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchAllPayments = (order_id) => {
  return useQuery({
    queryKey: ["order_id", order_id],
    queryFn: () => fetchAllPayments(order_id),
    enabled: !!order_id,
  });
};

const editLoanId = async (payload) => {
  const url = new URL(`${getOneOmsBaseUrl()}/edit-loan-id`);
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  }
  if (data.detail && Array.isArray(data.detail) && data.detail.length > 0) {
    const messages = data.detail
      .map((err) => err.msg)
      .filter(Boolean)
      .join(" • ");
    throw new Error(messages);
  }
  throw new Error(
    data?.message ||
      "Oops! Something went wrong. Please refresh the page and try again.",
  );
};
export const useEditLoanId = () => {
  return useMutation({ mutationFn: editLoanId });
};

const cancelLoan = async (loan_id) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/cancel-loan/${loan_id}`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useCancelLoan = () => {
  return useMutation({ mutationFn: cancelLoan });
};

const createLoanIntent = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/create-loan-intent`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  }

  if (data.detail && Array.isArray(data.detail)) {
    const messages = data.detail
      .map((errorDetail) => errorDetail.msg)
      .join("\n");
    const err = new Error(messages);
    if (data.error_code) err.error_code = data.error_code;
    throw err;
  }

  const err = new Error(
    data?.message || "Oops! Something went wrong while creating loan intent.",
  );
  if (data?.error_code) err.error_code = data.error_code;
  throw err;
};

export const useCreateLoanIntent = () => {
  return useMutation({ mutationFn: createLoanIntent });
};

const verticalAndTenantsId = async () => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-all-vertical-and-tenants`,
  );
  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useVerticalAndTenantsId = () => {
  return useQuery({
    queryKey: ["verticalAndTenantsId", "with-gateway-branch"],
    queryFn: () => verticalAndTenantsId(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const fetchGatewayTenants = async (gatewayTenantIds) => {
  const url = new URL(
    `${process.env.REACT_APP_PACKAGE_MANAGEMENT_URL}/get-one-oms-tenants-from-gateway-tenants`,
  );

  gatewayTenantIds.forEach((gatewayTenantId) => {
    url.searchParams.append("gateway_tenant_id", gatewayTenantId);
  });

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_PACKAGE_MANAGEMENT_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  const data = await response.json();

  if (data.status === "success" && data.data) {
    return Object.entries(data.data).map(([gatewayTenantId, tenant]) => ({
      gatewayTenantId,
      id: tenant.id,
      name: tenant.name,
      verticalId: tenant.vertical?.id,
    }));
  }

  throw new Error(
    data.message || "An error occurred while fetching tenant details.",
  );
};

export const useFetchGatewayTenants = (verticalId, gatewayTenantIds) =>
  useQuery({
    queryKey: ["gatewayTenants", verticalId, gatewayTenantIds],
    queryFn: () => fetchGatewayTenants(gatewayTenantIds),
    enabled: Boolean(verticalId) && gatewayTenantIds.length > 0,
    staleTime: DAY,
  });

const callCustomer = async (payload) => {
  const url = new URL(`${CALL_API_ENDPOINT}`);

  const headers = {
    ...gatewayTenantHeader(),
    "Auth-token": getAuthToken(),
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }

  if (data.status === "SUCCESS") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCallCustomer = () => {
  return useMutation({ mutationFn: callCustomer });
};

const fetchPartPaymentDetails = async ({ order_id, provider_id }) => {
  const url = `${getOneOmsBaseUrl()}/get-part-payment-schedules-v2`;

  const headers = oneOmsHeaders();

  const payload = { order_id, provider_id };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (response.ok && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message ||
        "An error occurred while fetching part payment schedules.",
    );
  }
};

export const useFetchPartPaymentDetails = (
  order_id,
  provider_id,
  options = {},
) => {
  const { enabled: enabledOverride } = options;
  const enabled =
    enabledOverride !== undefined
      ? enabledOverride
      : !!order_id && !!provider_id;
  return useQuery({
    queryKey: ["partPaymentDetails", order_id, provider_id],
    queryFn: () => fetchPartPaymentDetails({ order_id, provider_id }),
    enabled: !!order_id && !!provider_id && enabled,
  });
};

const createPartPaymentSchedule = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/create-part-payment-schedules-v2`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    const backendMsg =
      Array.isArray(data?.detail) && data.detail.length > 0
        ? data.detail[0].msg
        : data?.detail?.message;

    throw new Error(backendMsg || data.message);
  }
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCreatePartPaymentSchedule = (payload) => {
  return useMutation({
    mutationFn: createPartPaymentSchedule,
  });
};

const fetchPartPaymentInstallmentTaxInvoice = async (installmentId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-installment-tax-invoice/${installmentId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || `Failed to fetch tax invoice.`);
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching tax invoice.",
    );
  }
};

export const useFetchPartPaymentInstallmentTaxInvoice = (installmentId) => {
  const query = useQuery({
    queryKey: ["installmentTaxInvoice", installmentId],
    queryFn: () => fetchPartPaymentInstallmentTaxInvoice(installmentId),
    enabled: !!installmentId,
  });
  return {
    ...query,
    fetchPartPaymentInstallmentTaxInvoice,
  };
};

const fetchVerifyKHUser = async (phone_number) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const payload = { isd_code: "+91", phone: phone_number };

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: getAuthToken(),
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  if (response.status !== 200) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  const data = await response.json();
  if (data.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }
  return data;
};

export const useFetchVerifyKHUser = () => {
  return useMutation({ mutationFn: fetchVerifyKHUser });
};

const bankList = async () => {
  const url = new URL(`${getOneOmsBaseUrl()}/get-banks-list`);
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useBankList = () => {
  return useQuery({
    queryKey: ["bankList"],
    queryFn: () => bankList(),
    staleTime: DAY,
    cacheTime: DAY,
    retry: QUERY_RETRY_ATTEMPTS,
  });
};

const updateCashStatus = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-cash-status`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateCashStatus = () => {
  return useMutation({ mutationFn: updateCashStatus });
};

const updateChequeStatus = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-cheque-status`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateChequeStatus = () => {
  return useMutation({ mutationFn: updateChequeStatus });
};

const checkPhoneNumberMatch = (data) => {
  const storedPhoneNumber = localStorage.getItem("phoneNumber");
  if (storedPhoneNumber && data.phone === storedPhoneNumber) {
    throw new Error("You can not create order for your own ID.");
  }
};

const fetchKHUser = async (uam_id) => {
  const url = process.env.REACT_APP_GET_VERIFY_USER;

  const payload = { uam_id };

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": "900",
    "tenant-id": "7",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (!response.ok) {
    const backendMsg =
      Array.isArray(data?.detail) && data.detail.length > 0
        ? data.detail[0].msg
        : data?.detail?.message;

    throw new Error(backendMsg || "Something went wrong.");
  }

  if (Array.isArray(data) && data.length === 0) {
    throw new Error("No user found for the provided phone number or UAM ID.");
  }
  checkPhoneNumberMatch(data.users[0]);
  return data.users[0];
};

const transformLearnUsers = (globalUsers) => {
  if (
    !globalUsers ||
    !globalUsers.accounts ||
    globalUsers.accounts.length === 0
  ) {
    return [];
  }

  const account = globalUsers.accounts[0];

  if (!Array.isArray(account.profiles)) {
    return null;
  }

  const matchingProfiles = account.profiles.filter(
    (profile) => Number(profile.vertical_id) === Number(LEARN_VERTICAL_ID),
  );

  if (matchingProfiles.length > 1) {
    throw new Error(
      "Seems like there is an issue with this customer's phone number/UAM ID. Please escalate this customer to the support team for resolution.",
    );
  }

  if (matchingProfiles.length === 1) {
    const profile = matchingProfiles[0];
    return {
      phone: account.phone || "",
      verticalId: profile.vertical_id,
      uamId: profile.uam_id,
    };
  }

  return null;
};

const fetchLearnUser = async (uam_id) => {
  const url = getSearchUserUrl();

  const payload = { uam_id: Number(uam_id) };

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": "1000",
    "tenant-id": "1",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const globalUsers = await response.json();

  if (response.status !== 200) {
    const backendMsg =
      Array.isArray(globalUsers?.detail) && globalUsers.detail.length > 0
        ? globalUsers.detail[0].msg
        : globalUsers?.detail?.message;

    throw new Error(backendMsg || "Something went wrong.");
  }

  const filtered_users_data = transformLearnUsers(globalUsers);

  if (filtered_users_data.length === 0) {
    throw new Error("No user found for the provided UAM ID.");
  }
  checkPhoneNumberMatch(filtered_users_data);
  return filtered_users_data;
};

const fetchUser = async (uamId, verticalId) => {
  if (Number(verticalId) === Number(LEARN_VERTICAL_ID)) {
    return fetchLearnUser(uamId);
  }
  if (Number(verticalId) === Number(KH_VERTICAL_ID)) {
    return fetchKHUser(uamId);
  }
};

export const useFetchUser = (uamId, verticalId, countryId) => {
  const payload = {
    phone_number: null,
    uam_id: uamId,
    selectedBusinessVertical: verticalId,
    country_id: countryId,
  };
  return useQuery({
    queryKey: [uamId, verticalId, countryId],
    queryFn: () => fetchCustomerInfo(payload),
  });
};

const updateBankTransferStatus = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-bank-transfer-status`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useUpdateBankTransferStatus = () => {
  return useMutation({ mutationFn: updateBankTransferStatus });
};

const fetchInvoiceDetails = async (paymentId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-payment-invoices-details/${paymentId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useFetchInvoiceDetails = (paymentId) => {
  return useQuery({
    queryKey: ["InvoiceDetails", paymentId],
    queryFn: () => fetchInvoiceDetails(paymentId),
  });
};

const fetchPaymentTaxInvoice = async (invoiceNumber) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-heads-payment-invoice/${invoiceNumber}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch tax invoice. Status: ${response.status}`,
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the tax invoice.",
    );
  }
};

export const useFetchPaymentTaxInvoice = (invoiceNumber) => {
  const query = useQuery({
    queryKey: ["paymentTaxInvoice", invoiceNumber],
    queryFn: () => fetchPaymentTaxInvoice(invoiceNumber),
    enabled: !!invoiceNumber,
  });
  return {
    ...query,
    fetchPaymentTaxInvoice,
  };
};

const fetchOrderPaymentFlows = async (orderId) => {
  const url = `${getOneOmsBaseUrl()}/order-part-payments-status/${orderId}`;

  const options = {
    method: "GET",
    headers: oneOmsHeaders(),
  };
  const data = await fetchJSON(url, options);
  return data || [];
};

export const useFetchOrderPaymentFlows = (orderId) => {
  return useQuery({
    queryKey: ["paymentFlows", orderId],
    queryFn: () => fetchOrderPaymentFlows(orderId),
    enabled: !!orderId,
  });
};

const switchPaymentProvider = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/switch-part-payment-provider`,
  );
  const headers = oneOmsHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};
export const useSwitchPaymentProvider = () => {
  return useMutation({ mutationFn: switchPaymentProvider });
};

const cancelMandate = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/cancel-mandate/${orderId}`,
  );

  const headers = oneOmsHeaders();

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });
  const data = await response.json();
  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useCancelMandate = () => {
  return useMutation({ mutationFn: cancelMandate });
};

const generateOtp = async (phoneNumberAndCaptcha) => {
  const url = new URL(
    `${process.env.REACT_APP_COMMSERVICE_URL}/api/v3/generate-otp`,
  );
  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    isd_code: phoneNumberAndCaptcha.isd_code,
    phone: phoneNumberAndCaptcha.phone,
    tenant_id: 1,
    product_id: 300,
    captcha_token: phoneNumberAndCaptcha.captcha_token,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data?.message ||
        "Oops! Something went wrong. Please refresh the page and try again.",
    );
  }
};

export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

const loginWithOtp = async (payload) => {
  const response = await fetch(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/encrypted-login-with-otp`,
    {
      method: "POST",
      headers: {
        ...gatewayTenantHeader(),
        "Content-Type": "application/json",
        platform: PLATFORM,
        "product-id": Number(LOGIN_PRODUCT_ID),
        "tenant-id": Number(LOGIN_TENANT_ID),
      },
      body: JSON.stringify({
        isd_code: payload.isd_code,
        phone: payload.phone,
        otp: payload.otp,
        product_id: REQUEST_PRODUCT_ID,
        tenant_id: LOGIN_TENANT_ID,
      }),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }
  const data = await response.json();

  return data.data;
};

export const useLoginWithOtp = () => useMutation({ mutationFn: loginWithOtp });

const changePassword = async (payload) => {
  const url = new URL(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/change-password
`,
  );
  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": REQUEST_PRODUCT_ID,
    "tenant-id": LOGIN_TENANT_ID,
    platform: PLATFORM,
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorBody = await response.json();
    const errorMessage = errorBody?.message || "Something went wrong.";
    throw new Error(errorMessage);
  }
  const data = await response.json();

  return data.data;
};

export const useChangePassword = () =>
  useMutation({ mutationFn: changePassword });

const getPrimaryContact = (email, phone) => {
  return email && email.trim().length > 0 ? email.trim() : phone;
};

const morphAgentData = (data) => {
  if (!data || !data.data || !Array.isArray(data.data.agents)) {
    return [];
  }
  return data.data.agents.map((agent) => ({
    key: agent.uam_id,
    label: `${agent.first_name} ${agent.last_name} , ${getPrimaryContact(
      agent.email,
      agent.phone,
    )}`,
    value: agent.uam_id,
  }));
};

const fetchSalesAgents = async (searchTerm) => {
  const url = new URL(
    `${process.env.REACT_APP_GATEWAY_URL}/api/v1/user/agents/search-agent`,
  );

  const headers = {
    ...gatewayTenantHeader(),
    accept: "application/json",
    "product-id": REQUEST_PRODUCT_ID,
    "tenant-id": LOGIN_TENANT_ID,
    platform: PLATFORM,
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const payload = {
    name: searchTerm,
    roles: ROLES_FOR_SEARCHING_AGENT,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();

  return morphAgentData(data);
};

export const useSearchSalesAgent = (searchTerm, enabled = true) => {
  const shouldFetch =
    enabled && typeof searchTerm === "string" && searchTerm.length >= 3;

  return useQuery({
    queryKey: ["agents", searchTerm],
    queryFn: () => fetchSalesAgents(searchTerm),
    enabled: shouldFetch,
  });
};

const oneOmsOrderModificationHeaders = () =>
  oneOmsHeaders({
    "x-platform": "web",
    "x-source-id": PLUTUS_SOURCE_ID,
  });

const orderModificationSummary = async (orderId) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/get-order-modification-summary/${orderId}`,
  );
  const headers = oneOmsOrderModificationHeaders();
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useOrderModificationSummary = (orderId) => {
  return useQuery({
    queryKey: ["orderModificationSummary", orderId],
    queryFn: () => orderModificationSummary(orderId),
    enabled: !!orderId,
  });
};

const cancelOrderModification = async (payload) => {
  const url = new URL(
    `${getOneOmsBaseUrl()}/update-order-modification-request-v2`,
  );
  const headers = {
    ...oneOmsOrderModificationHeaders(),
    ...gatewayTenantHeader(),
  };
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};
export const useCancelOrderModification = () => {
  return useMutation({ mutationFn: cancelOrderModification });
};

const handleSendModificationLinkError = async (response) => {
  if (response.status === 401 || response.status === 403) {
    throw new Error(
      "Unauthorized to send modification link. Please login again.",
    );
  }
  if (response.status >= 500) {
    throw new Error("An error occurred while processing the request.");
  }
  try {
    const data = await response.json();
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  } catch {
    throw new Error(
      "An error occurred while processing the request. Please try again later.",
    );
  }
};

const sendModificationLink = async (orderId) => {
  const orderModificationDetails = await orderModificationSummary(orderId);
  const proposal_id = orderModificationDetails.proposal_order?.id;
  if (!proposal_id) {
    throw new Error("No proposal ID found for the order.");
  }
  const url = new URL(
    `${getOneOmsBaseUrl()}/send-order-modification-comm-event?proposal_id=${proposal_id}`,
  );
  const headers = oneOmsOrderModificationHeaders();
  const response = await fetch(url, {
    method: "POST",
    headers: headers,
  });

  if (!response.ok) {
    await handleSendModificationLinkError(response);
  }

  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.message;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request.",
    );
  }
};

export const useSendModificationLink = () => {
  return useMutation({ mutationFn: sendModificationLink });
};
